import { ExceptionOutlined } from "@ant-design/icons";
import { Text, VStack } from "@chakra-ui/react";
import { Alert, Button, Form, InputNumber, Modal, Select, Space, Tooltip } from "antd";
import { Rule } from "antd/lib/form";
import axios from "axios";
import { useAuthenticatorBulkFulfill, useAuthenticatorBulkMissingLabel } from "hooks/useAuthenticatorBulkMissingLabel";
import { useEffect, useState } from "react";

interface FulfillForm {
    batchId: string;
    length: number;
    width: number;
    height: number;
    weightInLbs: number;
}
const dimensionRules: Rule[] = [{ required: true, type: 'number', min: 1 }];

const AuthenticatorBulkFulfillment = () => {
    const [visible, setVisible] = useState<boolean>(false);
    const { data, refetch } = useAuthenticatorBulkMissingLabel(visible);
    const { fulfill } = useAuthenticatorBulkFulfill();
    const [working, setWorking] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const [pdfUrl, setPdfUrl] = useState<string>();
    const [labelCount, setLabelCount] = useState(0);
    const [form] = Form.useForm<FulfillForm>();

    const batchId = Form.useWatch('batchId', form);

    useEffect(() => {
        let count = 0;

        if (batchId) {
            const batch = data?.find(x => x.id === batchId)
            count = batch?.labelCount ?? 0
        }

        setLabelCount(count);
    }, [batchId, data]);

    const reset = async () => {
        setSuccess(false);
        setPdfUrl(undefined);
        form.resetFields();
        await refetch();
    }

    const close = () => {
        setVisible(false);
        reset();
    }

    const submit = async (data: FulfillForm) => {
        setError(undefined);

        try {
            setWorking(true);
            const { labelUrl } = await fulfill(data);
            setPdfUrl(labelUrl);
            setSuccess(true);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errData = error.response!.data as any;
                setError(errData?.detail ?? "Unable to purchase shipping label!");
            }
        } finally {
            setWorking(false);
        }
    }

    return <>
        <Tooltip placement="bottom" title="Authenticator Bulk Manual Fulfillment">
            <Button type="dashed" shape="circle" icon={<ExceptionOutlined />} size="large" onClick={() => setVisible(true)} style={{ marginRight: '15px' }} />
        </Tooltip>
        <Modal
            title="Authenticator Bulk Manual Fulfillment"
            closable={false}
            keyboard={false}
            maskClosable={false}
            destroyOnClose
            open={visible}
            footer={<Button key='cancel' type="default" onClick={close} disabled={working}>Close</Button >}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={submit}
            >
                <Form.Item>
                    <Text>
                        This will purchase a shipping label & send fulfillment requests to eBay for Authenticator Bulk batches.
                    </Text>
                </Form.Item>

                <Form.Item
                    name="batchId"
                    label="Authenticator Bulk Batch" rules={[{ required: true, message: "You must select a batch to fulfill!" }]}
                    help={labelCount > 2 && <Alert showIcon type="warning" message="This batch may already have already been fulfilled!" />}
                >
                    <Select options={data?.map(x => ({ label: x.name, value: x.id }))} disabled={!data || success} />
                </Form.Item>

                <Space direction="horizontal" size={20}>
                    <Form.Item name="length" label="Length" rules={dimensionRules}>
                        <InputNumber placeholder="Inches" />
                    </Form.Item>
                    <Form.Item name="width" label="Width" rules={dimensionRules}>
                        <InputNumber placeholder="Inches" />
                    </Form.Item>
                    <Form.Item name="height" label="Height" rules={dimensionRules}>
                        <InputNumber placeholder="Inches" />
                    </Form.Item>
                    <Form.Item name="weightInLbs" label="Weight (lbs.)" rules={dimensionRules}>
                        <InputNumber placeholder="Pounds" />
                    </Form.Item>
                </Space>

                <Form.Item>
                    {!success && <Button htmlType="submit" type="primary" block disabled={working} loading={working}>Fulfill Batch Shipments</Button>}
                    {success && <VStack>
                        <Alert
                            type="success"
                            showIcon
                            message="Label purchased & fulfillments have been queued for processing!"
                            description={<Button
                                type="link"
                                href={pdfUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ float: 'inline-end', marginRight: '5px' }}>
                                Open Label PDF
                            </Button>}
                        />
                        <Button onClick={reset} type="primary">Reset for another fulfillment</Button>
                    </VStack>}
                </Form.Item>

                {error && <Alert
                    type="error"
                    showIcon
                    message={error}
                />}
            </Form>
        </Modal>
    </>;
}

export default AuthenticatorBulkFulfillment;